import MenuBookIcon from '@mui/icons-material/MenuBook';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import ArticleIcon from '@mui/icons-material/Article';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import TheatersIcon from '@mui/icons-material/Theaters';
import TheaterComedyIcon from '@mui/icons-material/TheaterComedy';
import PersonPinIcon from '@mui/icons-material/PersonPin';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import FolderIcon from '@mui/icons-material/Folder';

/**
 * Central configuration for every project type the server supports.
 *
 * Each entry decides:
 *  - how the type is presented (label, description, icon)
 *  - the writing terminology used across the app (unit = top-level container,
 *    subUnit = the editable block inside it; both map onto chapters/scenes
 *    in the data model)
 *  - which workspace sections make sense for the type
 */
const PROJECT_TYPE_CONFIG = {
  novel: {
    label: 'Novel',
    description: 'A full-length work of fiction organized into chapters and scenes.',
    icon: MenuBookIcon,
    sectionLabel: 'Manuscript',
    unitLabel: 'Chapter',
    unitPlural: 'Chapters',
    subUnitLabel: 'Scene',
    subUnitPlural: 'Scenes',
    unitPlaceholder: 'e.g., Chapter 1, Prologue, Epilogue',
    subUnitPlaceholder: 'e.g., Opening Scene, Character Introduction',
    showCharacters: true,
    showGameDesign: false,
  },
  short_story: {
    label: 'Short Story',
    description: 'A shorter piece of fiction, organized into parts and scenes.',
    icon: AutoStoriesIcon,
    sectionLabel: 'Manuscript',
    unitLabel: 'Part',
    unitPlural: 'Parts',
    subUnitLabel: 'Scene',
    subUnitPlural: 'Scenes',
    unitPlaceholder: 'e.g., Part 1, Opening',
    subUnitPlaceholder: 'e.g., Opening Scene, The Turn',
    showCharacters: true,
    showGameDesign: false,
  },
  poetry: {
    label: 'Poetry',
    description: 'A collection of poems, each with its own sections or drafts.',
    icon: HistoryEduIcon,
    sectionLabel: 'Collection',
    unitLabel: 'Poem',
    unitPlural: 'Poems',
    subUnitLabel: 'Section',
    subUnitPlural: 'Sections',
    unitPlaceholder: 'e.g., Ode to Autumn, Untitled #3',
    subUnitPlaceholder: 'e.g., First Movement, Refrain',
    showCharacters: false,
    showGameDesign: false,
  },
  essay: {
    label: 'Essay',
    description: 'A nonfiction piece organized into sections and passages.',
    icon: ArticleIcon,
    sectionLabel: 'Essay',
    unitLabel: 'Section',
    unitPlural: 'Sections',
    subUnitLabel: 'Passage',
    subUnitPlural: 'Passages',
    unitPlaceholder: 'e.g., Introduction, Argument, Conclusion',
    subUnitPlaceholder: 'e.g., Thesis, Supporting Evidence',
    showCharacters: false,
    showGameDesign: false,
  },
  article: {
    label: 'Article',
    description: 'A journalistic or informational piece with sections and passages.',
    icon: NewspaperIcon,
    sectionLabel: 'Article',
    unitLabel: 'Section',
    unitPlural: 'Sections',
    subUnitLabel: 'Passage',
    subUnitPlural: 'Passages',
    unitPlaceholder: 'e.g., Lede, Body, Conclusion',
    subUnitPlaceholder: 'e.g., Opening Hook, Key Findings',
    showCharacters: false,
    showGameDesign: false,
  },
  screenplay: {
    label: 'Screenplay',
    description: 'A script for film or television, organized into acts and scenes.',
    icon: TheatersIcon,
    sectionLabel: 'Script',
    unitLabel: 'Act',
    unitPlural: 'Acts',
    subUnitLabel: 'Scene',
    subUnitPlural: 'Scenes',
    unitPlaceholder: 'e.g., Act I, Cold Open',
    subUnitPlaceholder: 'e.g., INT. COFFEE SHOP - DAY',
    showCharacters: true,
    showGameDesign: false,
  },
  play: {
    label: 'Play',
    description: 'A stage play, organized into acts and scenes.',
    icon: TheaterComedyIcon,
    sectionLabel: 'Script',
    unitLabel: 'Act',
    unitPlural: 'Acts',
    subUnitLabel: 'Scene',
    subUnitPlural: 'Scenes',
    unitPlaceholder: 'e.g., Act I, Act II',
    subUnitPlaceholder: 'e.g., Scene 1, The Balcony',
    showCharacters: true,
    showGameDesign: false,
  },
  memoir: {
    label: 'Memoir',
    description: 'A personal narrative organized into chapters and scenes.',
    icon: PersonPinIcon,
    sectionLabel: 'Manuscript',
    unitLabel: 'Chapter',
    unitPlural: 'Chapters',
    subUnitLabel: 'Scene',
    subUnitPlural: 'Scenes',
    unitPlaceholder: 'e.g., Chapter 1, The Early Years',
    subUnitPlaceholder: 'e.g., First Day of School',
    showCharacters: true,
    showGameDesign: false,
  },
  biography: {
    label: 'Biography',
    description: "An account of someone's life, organized into chapters and scenes.",
    icon: RecordVoiceOverIcon,
    sectionLabel: 'Manuscript',
    unitLabel: 'Chapter',
    unitPlural: 'Chapters',
    subUnitLabel: 'Scene',
    subUnitPlural: 'Scenes',
    unitPlaceholder: 'e.g., Chapter 1, Origins',
    subUnitPlaceholder: 'e.g., The Turning Point',
    showCharacters: true,
    showGameDesign: false,
  },
  video_game: {
    label: 'Video Game',
    description: 'Game narrative plus design tools: ERD, type charts, and formulas.',
    icon: SportsEsportsIcon,
    sectionLabel: 'Narrative',
    unitLabel: 'Chapter',
    unitPlural: 'Chapters',
    subUnitLabel: 'Scene',
    subUnitPlural: 'Scenes',
    unitPlaceholder: 'e.g., Chapter 1, Tutorial Island',
    subUnitPlaceholder: 'e.g., Opening Cutscene, Boss Intro',
    showCharacters: true,
    showGameDesign: true,
  },
  other: {
    label: 'Other',
    description: 'A general writing project with chapters and scenes.',
    icon: FolderIcon,
    sectionLabel: 'Manuscript',
    unitLabel: 'Chapter',
    unitPlural: 'Chapters',
    subUnitLabel: 'Scene',
    subUnitPlural: 'Scenes',
    unitPlaceholder: 'e.g., Chapter 1, Section A',
    subUnitPlaceholder: 'e.g., Scene 1',
    showCharacters: true,
    showGameDesign: false,
  },
};

/**
 * Returns the config for a project type, falling back to 'other' for unknown types
 * so callers never need null checks.
 */
export const getProjectTypeConfig = (type) =>
  PROJECT_TYPE_CONFIG[type] || PROJECT_TYPE_CONFIG.other;

/** Options for type selects: [{ value, label, description, icon }] */
export const PROJECT_TYPE_OPTIONS = Object.entries(PROJECT_TYPE_CONFIG).map(([value, config]) => ({
  value,
  label: config.label,
  description: config.description,
  icon: config.icon,
}));

export default PROJECT_TYPE_CONFIG;
